import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import AdminLogin from './AdminLogin';
import InquiriesTab from './tabs/InquiriesTab';
import VisaTab from './tabs/VisaTab';
import ItineraryTab from './tabs/ItineraryTab';
import NavbarTab from './tabs/NavbarTab';
import FeedbackTab from './tabs/FeedbackTab';
import QuotationTab from './tabs/QuotationTab';
import ShortBreaksTab from './tabs/ShortBreaksTab';
import './admin.css';
const TABS = [
    { id: 'inquiries', label: 'Inquiries', icon: 'fa-solid fa-inbox' },
    { id: 'visa', label: 'Visa Manager', icon: 'fa-solid fa-passport' },
    { id: 'itinerary', label: 'Itinerary', icon: 'fa-solid fa-map-location-dot' },
    { id: 'shortbreaks', label: 'Short Breaks', icon: 'fa-solid fa-calendar-days' },
    { id: 'navbar', label: 'Holidays Menu', icon: 'fa-solid fa-bars' },
    { id: 'feedback', label: 'Feedback', icon: 'fa-solid fa-star' },
    { id: 'quotation', label: 'Quotation', icon: 'fa-solid fa-file-word' },
];

const Toast = ({ toast }) => toast ? (
    <div className={`admin-toast ${toast.type}`}>
        <i className={toast.type === 'success' ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-exclamation'} />
        {toast.msg}
    </div>
) : null;

const AdminApp = () => {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('inquiries');
    const [toast, setToast] = useState(null);
    const [newInqCount, setNewInqCount] = useState(0);

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setLoading(false);
        });
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        if (!session) return;
        const fetchNewCount = async () => {
            const { count } = await supabase.from('inquiries').select('*', { count: 'exact', head: true }).eq('status', 'new');
            setNewInqCount(count || 0);
        };
        fetchNewCount();
        const channel = supabase.channel('new-inquiries')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'inquiries' }, () => {
                fetchNewCount();
                showToast('New inquiry received!', 'success');
            })
            .subscribe();
        return () => supabase.removeChannel(channel);
    }, [session]);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        setSession(null);
    };

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', background: '#070d1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
                <div className="admin-loading"><div className="admin-spinner" /><p>Loading admin panel...</p></div>
            </div>
        );
    }

    if (!session) return <AdminLogin onLogin={() => supabase.auth.getSession().then(({ data: { session } }) => setSession(session))} />;

    return (
        <div className="admin-dashboard">
            {/* Top Bar */}
            <header className="admin-topbar">
                <div className="admin-topbar-left">
                    <div className="admin-topbar-icon">✈️</div>
                    <div>
                        <div className="admin-topbar-title">Silver Sky Holidays</div>
                        <div className="admin-topbar-sub">Admin Dashboard</div>
                    </div>
                </div>
                <div className="admin-topbar-right">
                    <span className="admin-topbar-badge"><i className="fa-solid fa-circle" style={{ color: '#10b981', fontSize: 8, marginRight: 5 }} />Live</span>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{session.user.email}</span>
                    <button className="admin-logout-btn" onClick={handleLogout}>
                        <i className="fa-solid fa-right-from-bracket" /> Logout
                    </button>
                </div>
            </header>

            {/* Tab Nav */}
            <nav className="admin-tabs-nav">
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        className={`admin-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        <i className={tab.icon} />
                        {tab.label}
                        {tab.id === 'inquiries' && newInqCount > 0 && (
                            <span className="admin-tab-count red">{newInqCount}</span>
                        )}
                    </button>
                ))}
            </nav>

            {/* Tab Content */}
            <div className="admin-content">
                {(() => {
                    switch (activeTab) {
                        case 'inquiries': return <InquiriesTab showToast={showToast} />;
                        case 'visa': return <VisaTab showToast={showToast} />;
                        case 'itinerary': return <ItineraryTab showToast={showToast} />;
                        case 'shortbreaks': return <ShortBreaksTab showToast={showToast} />;
                        case 'navbar': return <NavbarTab showToast={showToast} />;
                        case 'feedback': return <FeedbackTab showToast={showToast} />;
                        case 'quotation': return <QuotationTab showToast={showToast} />;
                        default: return null;
                    }
                })()}
            </div>

            <Toast toast={toast} />
        </div>
    );
};

export default AdminApp;
